import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { WeeklyDatePicker } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { classes } from './WeeklyDatePicker.visual.st.css';
import { EditorShell, VisualAsync } from '../../test/visual/VisualAsync';
import {
  WeeklyDatePickerDriver,
  weeklyDatePickerDriverFactory,
} from './WeeklyDatePicker.driver';

const VisualTestDatePicker = (props) => {
  const { width, ...rest } = props;
  return (
    <EditorShell>
      <div style={{ width }}>
        <WeeklyDatePicker {...rest} />
      </div>
    </EditorShell>
  );
};
const baseDate = new Date('2022/06/14');
const baseProps = {
  width: '600px',
  value: baseDate,
  onChange: () => {},
};

const render = (overrides: Record<string, any> = {}) => (
  <VisualTestDatePicker {...baseProps} {...overrides} />
);
const renderRtl = (overrides: Record<string, any> = {}) => (
  <div dir="rtl">
    <TPAComponentsProvider value={{ rtl: true }}>
      {render(overrides)}
    </TPAComponentsProvider>
  </div>
);

visualize('WeeklyDatePicker', () => {
  story('simple', () => {
    snap('default props', render({ width: '320px' }));
    snap('responsiveness', render());
    snap(
      'FilterDate - Prior for today dates (includes today)',
      render({ filterDate: (date) => date <= baseDate }),
    );
    snap(
      'FilterDate - future dates only (includes today)',
      render({ filterDate: (date) => date >= baseDate }),
    );
    snap(
      'Date Indication',
      render({
        value: new Date('2022/06/12'),
        dateIndication: ({ date }) =>
          date <= baseDate ? (
            <div className={classes.allIndications}>
              <div className={classes.indication} />
            </div>
          ) : null,
      }),
    );
    snap(
      'Adaptive to font change',
      render({ className: classes.dayFontSizeOverride, width: '650px' }),
    );
    snap(
      'Selected day border radius',
      render({ className: classes.selectedDayBorderRadius }),
    );
    snap(
      'with overrides',
      render({
        className: classes.styled,
        filterDate: (date) => date >= baseDate,
      }),
    );
    snap('No outer padding', render({ removeOuterPadding: true }));
    snap('Today as a prop', render({ today: new Date('2022/06/12') }));
    snap('responsive rtl', renderRtl({ locale: 'he', firstDayOfWeek: 0 }));
  });
  story('arrowLocation', () => {
    snap('arrows next to dates', render({ arrowLocation: 'dates' }));
    snap('arrows next to caption', render({ arrowLocation: 'caption' }));
    snap(
      'arrows next to dates with rtl',
      renderRtl({ arrowLocation: 'dates' }),
    );
    snap(
      'arrows next to caption with rtl',
      renderRtl({ arrowLocation: 'caption' }),
    );
  });
  story('groupWeekdaysAndDates', () => {
    snap('grouped layout', render({ groupWeekdaysAndDates: true }));
    snap('ungrouped layout', render({ groupWeekdaysAndDates: false }));
    snap('grouped layout with rtl', renderRtl({ groupWeekdaysAndDates: true }));
    snap(
      'ungrouped layout with rtl',
      renderRtl({ groupWeekdaysAndDates: false }),
    );
  });

  story('wst overrides', () => {
    snap(
      'background color',
      render({ className: classes.wstOverrideBackground }),
    );
    snap('text color', render({ className: classes.wstOverrideText }));
    snap('font', render({ className: classes.wstOverrideFont }));
  });

  // TODO: JB: unskip tests,
  // skip tests for now because snap doesn't preserve state when done is called
  false &&
    visualize('WeeklyDatePicker', () => {
      story('simple / focus', () => {
        const props = {};
        const its = [
          {
            it: 'previous button',
            props,
            action: async (driver: WeeklyDatePickerDriver) =>
              driver.clickOnPrevWeekButton(),
          },
          {
            it: 'next button',
            props,
            action: async (driver: WeeklyDatePickerDriver) =>
              driver.clickOnNextWeekButton(),
          },
          {
            it: 'selected day',
            props,
            action: async (driver: WeeklyDatePickerDriver) =>
              driver.clickDay(new Date('2022/06/14')),
          },
        ];

        its.forEach(({ it }) =>
          snap(it, (done) => (
            <VisualAsync
              driverFactory={weeklyDatePickerDriverFactory}
              onDone={done}
              action={(driver) => driver.clickOnPrevWeekButton()}
            >
              <WeeklyDatePicker
                value={new Date('2022/06/14')}
                onChange={() => {}}
              />
            </VisualAsync>
          )),
        );
      });
    });
});
